import { Rect } from '../types';

export interface SvgImageDimensions {
  naturalWidth: number;
  naturalHeight: number;
  renderWidth: number;
  renderHeight: number;
  svgLeft: number;
  svgTop: number;
}

export interface Coords {
  x: number;
  y: number;
}

/**
 * Convert coordinates from natural image space to display (SVG) space
 */
export function convertOriginalToDisplayCoords(
  originalX: number,
  originalY: number,
  dimensions: SvgImageDimensions
): Coords {
  return {
    x: (originalX / dimensions.naturalWidth) * dimensions.renderWidth,
    y: (originalY / dimensions.naturalHeight) * dimensions.renderHeight,
  };
}

/**
 * Convert shape (Rect) from natural image space to display space
 */
export function convertShapeToDisplay(shape: Rect, dimensions: SvgImageDimensions): Rect {
  const [x, y, width, height] = shape;
  return [
    (x / dimensions.naturalWidth) * dimensions.renderWidth,
    (y / dimensions.naturalHeight) * dimensions.renderHeight,
    (width / dimensions.naturalWidth) * dimensions.renderWidth,
    (height / dimensions.naturalHeight) * dimensions.renderHeight,
  ];
}

/**
 * Convert coordinates from display (SVG) space to natural image space
 */
export function convertDisplayToOriginalCoords(
  displayX: number,
  displayY: number,
  dimensions: SvgImageDimensions
): Coords {
  return {
    x: (displayX / dimensions.renderWidth) * dimensions.naturalWidth,
    y: (displayY / dimensions.renderHeight) * dimensions.naturalHeight,
  };
}

/**
 * Convert shape (Rect) from display space to natural image space
 */
export function convertShapeToOriginal(shape: Rect, dimensions: SvgImageDimensions): Rect {
  const [x, y, width, height] = shape;
  return [
    (x / dimensions.renderWidth) * dimensions.naturalWidth,
    (y / dimensions.renderHeight) * dimensions.naturalHeight,
    (width / dimensions.renderWidth) * dimensions.naturalWidth,
    (height / dimensions.renderHeight) * dimensions.naturalHeight,
  ];
}

/**
 * Get corner coordinates from a Rect shape
 * Returns corners in order: top-left, top-right, bottom-right, bottom-left
 */
export function getCornersFromShape(shape: Rect): Coords[] {
  const [x, y, width, height] = shape;
  return [
    { x, y }, // top-left
    { x: x + width, y }, // top-right
    { x: x + width, y: y + height }, // bottom-right
    { x, y: y + height }, // bottom-left
  ];
}

/**
 * Convert corner positions back to a Rect shape
 * Assumes corners are in order: top-left, top-right, bottom-right, bottom-left
 */
export function getShapeFromCorners(corners: Coords[]): Rect {
  if (corners.length !== 4) {
    throw new Error('Expected 4 corners');
  }

  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);

  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);

  return [minX, minY, maxX - minX, maxY - minY];
}

/**
 * Extract coordinates from mouse or touch event
 */
export function getEventCoordinates(event: React.MouseEvent | React.TouchEvent): {
  clientX: number;
  clientY: number;
} {
  if ('touches' in event && event.touches && event.touches.length > 0) {
    return {
      clientX: event.touches[0].clientX,
      clientY: event.touches[0].clientY,
    };
  }
  const mouseEvent = event as React.MouseEvent;
  return {
    clientX: mouseEvent.clientX,
    clientY: mouseEvent.clientY,
  };
}
